/* eslint-disable @typescript-eslint/no-explicit-any */

import { Button, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';

import { apiAdminGenerateVeteransResume, VeteransResumeGenerationProgress } from '@/api/services';
import FileUploadCard from '@/components/forms/FileUploadCard';
import Resume from '@/components/data-display/Resume';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { exportResumeToDocx } from '@/utils/file';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const MAX_FILE_SIZE_MB = MAX_RESUME_FILE_SIZE_MB;

export default function VeteransResumeGenerationSection() {
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState<VeteransResumeGenerationProgress | null>(null);
  const {
    resumeFile,
    setResumeFile,
    clearResumeFile,
    resume,
    setResume,
    clearResume,
    clearResumeAnalysisResult
  } = useAdminAnalysis();

  const handleFileUpload = async (file: File) => {
    setResumeFile(file);
    setFileUploading(true);
    clearResume();
    clearResumeAnalysisResult();
    setFileError(null);
    setProgress(null);

    if (file.size > FILE_SIZE_LIMIT) {
      setFileError(`File size exceeds ${MAX_FILE_SIZE_MB} MB`);
      setFileUploading(false);
      return;
    }

    try {
      const response = await apiAdminGenerateVeteransResume({
        payload: { file },
        onProgress: (p) => setProgress(p)
      });

      const isSuccess = response.code === 200 || response.code === 201;
      if (isSuccess) {
        setResume(response.data);
      } else {
        handleFileError('Unable to extract resume', response.message);
      }
    } catch (error) {
      handleFileError('Unable to extract resume', error);
    } finally {
      setFileUploading(false);
      setProgress(null);
    }
  };

  const handleFileError = (message: string, error: any) => {
    console.error(message, error);
    setFileError(message);
  };

  const resetFileState = () => {
    clearResumeFile();
    clearResume();
    clearResumeAnalysisResult();
    setFileError(null);
    setProgress(null);
  };

  const handleExportDocx = () => {
    if (!resume) return;
    exportResumeToDocx(resume, `${resume.personal_information.name || 'resume'}.docx`);
  };

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px" align="flex-start" width="100%">
        <FileUploadCard
          file={resumeFile}
          uploading={fileUploading}
          error={fileError}
          maxSizeMB={MAX_FILE_SIZE_MB}
          progress={progress}
          onChange={handleFileUpload}
          onReset={resetFileState}
        />
      </Stack>
      <Stack direction="row" justify="space-between" align="center">
        <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
          Extracted Resume
        </Text>
        {resume && (
          <Button
            // size="sm"
            onClick={handleExportDocx}
            borderRadius="8px"
            p="12px 24px"
            fontSize="16px"
          >
            Download
          </Button>
        )}
      </Stack>
      {resume ? <Resume resume={resume} /> : <Text>No resume extracted yet.</Text>}
    </Stack>
  );
}
